/**
 * Button 모듈 (installed)
 * @module shared/components/ui/button
 * @author Olive
 */

import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import {
  cva,
  type VariantProps,
} from 'class-variance-authority';

import cn from '@/shared/components/lib/utils.ts';

/**
 * Button Variants
 * * variants: 버튼 종류 (default, destructive, outline, secondary, ghost, link)
 * * size: 버튼 크기 (default, sm, lg, icon)
 * @author Olive
 */
const buttonVariants = cva('', {
  variants: {
    variant: {
      default: 'p-2 bg-[#eee] dark:bg-[#3f4454] text-[#000] dark:text-[#fff] rounded-[8px]', // 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
      primary: 'p-2 bg-[#4e12ff] dark:bg-[#4e12ff] hover-bg-[#3814a4] hover:dark:bg-[#3814a4] rounded-[8px]',
      destructive: '', // 'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
      outline: '', // 'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
      secondary: '', // 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
      ghost: '', // 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
      link: '', // 'text-primary underline-offset-4 hover:underline',
      paging:
        'block text-center text-[#858585] rounded-[3px] data-[active=true]:bg-[#c7c7c7] dark:data-[active=true]:bg-[#3f4454] dark:data-[active=true]:text-[#d7dce2]',
      emphasize: 'p-2 text-[#fff] bg-[#4212ff] hover:bg-[#3814a4] rounded-[8px]',
    },
    size: {
      default: '', // 'h-9 px-4 py-2 has-[>svg]:px-3',
      sm: '', // 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
      lg: '', // 'h-10 rounded-md px-6 has-[>svg]:px-4',
      icon: 'size-16', // 'size-9',
      paging: 'w-[24px] h-[24px]',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
});

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
